type MapMode = 'forward' | 'reverse';

type BuildInput = {
    openArray: number[];
    closeArray: number[];
    isCustomDrehwinkel: boolean;
    winkelKraftObject: any;
    mode: MapMode;
};

const clampIndex = (idx: number) => Math.max(0, Math.min(100, idx));

const angleToIndex = (angle: number, mode: MapMode): number => {
    const normalized = Math.round(angle);
    if (mode === 'forward') {
        // 90° -> 5, 45° -> 50, 0° -> 95
        return clampIndex(95 - normalized);
    }
    // 90° -> 95, 45° -> 50, 0° -> 5
    return clampIndex(normalized + 5);
};

/**
 * Maps UI `bisWinkel` (°) to `mZylArray` / `klappeOpen` column index.
 * Used for sizing (klappe profile, max-torque checks) — not for the performance table.
 */
export const winkelToTableIndex = (angle: number, mode: MapMode): number => angleToIndex(angle, mode);

const getValue = (arr: number[], idx: number): number => {
    if (!Array.isArray(arr) || idx < 0 || idx >= arr.length) return 0;
    return arr[idx];
};

const getDefaultIndexes = (mode: MapMode) => {
    if (mode === 'reverse') {
        return { bto: 5, run: 50, eto: 95, btc: 95, runOut: 50, etc: 5 };
    }
    return { bto: 95, run: 50, eto: 5, btc: 5, runOut: 50, etc: 95 };
};

const getCatalogAngles = (mode: MapMode) => {
    if (mode === 'reverse') {
        return { bto: 90, run: 45, eto: 0, btc: 0, runOut: 45, etc: 90 };
    }
    return { bto: 0, run: 45, eto: 90, btc: 90, runOut: 45, etc: 0 };
};

/**
 * Catalog points for the performance table: BTO/ETC at 0°, RUN at 45°, ETO/BTC at 90°.
 *
 * Custom `bisWinkel` values describe the valve-requirement zones used for sizing
 * (klappe). They are not the sample points for Act BTO/RTO/ETO in the datasheet.
 */
export const buildAntriebDrehsObj = ({
    openArray,
    closeArray,
    mode
}: BuildInput) => {
    const i = getDefaultIndexes(mode);
    const a = getCatalogAngles(mode);
    return {
        bto: getValue(openArray, i.bto),
        run: getValue(openArray, i.run),
        eto: getValue(openArray, i.eto),
        btc: getValue(closeArray, i.btc),
        runOut: getValue(closeArray, i.runOut),
        etc: getValue(closeArray, i.etc),
        bto_winkel: a.bto,
        run_winkel: a.run,
        eto_winkel: a.eto,
        btc_winkel: a.btc,
        runOut_winkel: a.runOut,
        etc_winkel: a.etc
    };
};
